import { permissionService } from "./services/PermissionService";
import { storageService } from "./services/StorageService";
import { routes as SideMenuRoutes, Route } from "./sidemenu";
import _ from "lodash";
import { jwtDecode } from "jwt-decode";
import { operatorsApiService } from "./services/api/users/OperatorApiService";
import { Roles, UserPermissions } from "./models/AppConstants";

const updatePermissions = async () => {
    storageService.setPermissions(((await permissionService.GetPermissions()).data));
    storageService.setSelectedMerchantId(storageService.getLoggedInUserMerchantId().toString())
};

const updateRoles = async () => {
    const userRoles = (await operatorsApiService.getRoles()).data.map(n => n.name);
    storageService.setRoles(userRoles);
};

const isSuperAdmin = () => {
    const token = storageService.getAccessToken();
    if (!token) return false;

    try {
        const roles = JSON.parse(atob(token.split(".")[1])).role || [];
        return roles.some((role: string) => role.toLowerCase() === Roles.SUPER_ADMIN.toLowerCase());
    } catch (error) {
        return false;
    }
};

// OIDC tokens carry the real operator id in the "sub" claim (confirmed against Auth.Server's own
// BaseApiController comment). Used to hide self-approval actions client-side — the backend
// (IndividualDocumentService.ConfirmAsync/RejectAsync) already blocks it independently.
const getCurrentOperatorUserId = (): number | null => {
    const token = storageService.getAccessToken();
    if (!token) return null;

    try {
        // jwtDecode handles base64url ("-", "_"), which atob does not. The stored token may carry a
        // "Bearer " prefix; strip it so the call works with or without one.
        const { sub } = jwtDecode<{ sub?: string }>(token.replace(/^Bearer\s+/i, ""));
        const userId = Number(sub);
        return Number.isFinite(userId) ? userId : null;
    } catch (error) {
        return null;
    }
};

const isDataManager = () => {
    // return true;
    const token = storageService.getAccessToken();
    if (!token) return false;

    try {
        const roles = JSON.parse(atob(token.split(".")[1])).role || [];
        return roles.some((role: string) => role.toLowerCase() === Roles.UserDataManager.toLowerCase());
    } catch (error) {
        return false;
    }
};

const canSoftDeleteUser = () => {
    // return true;
    const token = storageService.getAccessToken();
    if (!token) return false;

    try {
        const roles = JSON.parse(atob(token.split(".")[1])).role || [];
        return roles.some((role: string) => role.toLowerCase() === Roles.SoftDeleteUser.toLowerCase());
    } catch (error) {
        return false;
    }
};
const isSuperAdminTeams = () => {
    const token = storageService.getAccessToken();
    if (!token) return false;

    try {
        const roles = JSON.parse(atob(token.split(".")[1])).role || [];
        return roles.some((role: string) =>
            [Roles.SUPER_ADMIN.toLowerCase(), Roles.MerchantTechnicalAccount.toLowerCase()].includes(role.toLowerCase())
        );
    } catch (error) {
        return false;
    }
};

const isDashboardAllowed = () => {
    return hasPermission(UserPermissions.DashboardAllowed);
};

const isMerchantDashboardAllowed = () => {
    return hasPermission(UserPermissions.MerchantDashboardAllowed);
};

const isMerchantDashboardUpdateAllowed = () => {
    return hasPermission(UserPermissions.MerchantDashboardUpdateAllowed);
};

const isCRMUserAllAllowed = () => {
    return hasPermission(UserPermissions.CRMUserAllAllowed);
};

const isCRMUserAllowed = () => {
    return hasPermission(UserPermissions.CRMUserAllowed);
};

const isCRMUserCreateNewAllowed = () => {
    return hasPermission(UserPermissions.CRMUserCreateNewAllowed);
};

const isUserProfileViewDetailsAllowed = () => {
    return hasPermission(UserPermissions.UserProfileViewDetailsAllowed);
};

const isUserPaymentMethodUpdateAllowed = () => {
    return hasPermission(UserPermissions.UserPaymentMethodUpdateAllowed);
};

const isMerchantAllAllowed = () => {
    return hasPermission(UserPermissions.MerchantAllAllowed);
};

const isMerchantCreateNewAllowed = () => {
    return hasPermission(UserPermissions.MerchantCreateNewAllowed);
};

const isMerchantUpdateAllowed = () => {
    return hasPermission(UserPermissions.MerchantUpdateAllowed);
};

const isMerchantPaymentMethodUpdateAllowed = () => {
    return hasPermission(UserPermissions.MerchantPaymentMethodUpdateAllowed);
};

const isIndividualPaymentMethodUpdateAllowed = () => {
    return hasPermission(UserPermissions.IndividualPaymentMethodUpdateAllowed);
};

const isIndividualDepositAllowed = () => {
    return hasPermission(UserPermissions.IndividualDepositAllowed);
};

const isIndividualWithdrawAllowed = () => {
    return hasPermission(UserPermissions.IndividualWithdrawAllowed);
};

const isMerchantAllowed = () => {
    return hasPermission(UserPermissions.MerchantAllowed);
};

const isMerchantClientsAllowed = () => {
    return hasPermission(UserPermissions.MerchantClientsAllowed);
};

const isMerchantViewDetailAllowed = () => {
    return hasPermission(UserPermissions.MerchantViewDetailAllowed);
};

const isWalletsViewAllowed = () => {
    return hasPermission(UserPermissions.WalletsViewAllowed);
};

const isWalletCreateAllowed = () => {
    return hasPermission(UserPermissions.WalletCreateAllowed);
};

const isIbanCreateAllowed = () => {
    return hasPermission(UserPermissions.IbanCreateAllowed);
};

const isWalletCloseAllowed = () => {
    return hasPermission(UserPermissions.WalletCloseAllowed);
};

const isTariffViewAllowed = () => {
    return hasPermission(UserPermissions.TariffViewAllowed);
};

const isTariffUpdateAllowed = () => {
    return hasPermission(UserPermissions.TariffUpdateAllowed);
};

const isKycSubmissionAllAllowed = () => {
    return hasPermission(UserPermissions.KycSubmissionAllAllowed);
};

const isKycSubmissionViewAllowed = () => {
    return hasPermission(UserPermissions.KycSubmissionViewAllowed);
};

const isKycSubmissionCreateAllowed = () => {
    return hasPermission(UserPermissions.KycSubmissionCreateAllowed);
};

const isKycDocumentAllAllowed = () => {
    return hasPermission(UserPermissions.KycDocumentAllAllowed);
};

const isJournalingAllowed = () => {
    return hasPermission(UserPermissions.JournalingAllowed);
};

const isPortalAdminAllowed = () => {
    return hasPermission(UserPermissions.PortalAdminAllowed);
};

const isCRMAllAllowed = () => {
    return hasPermission(UserPermissions.CRMAllAllowed);
};

const areOperatorsAndRolesAllAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesAllAllowed);
};

const areOperatorsAndRolesViewUserAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesViewUserAllowed);
};

const areOperatorsAndRolesCreateNewOperatorAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesCreateNewOperatorAllowed);
};

const areOperatorsAndRolesUpdateOperatorAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesUpdateOperatorAllowed);
};

const areOperatorsAndRolesViewUsersRolesAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesViewUsersRolesAllowed);
};

const areOperatorsAndRolesCreateNewRoleAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesCreateNewRoleAllowed);
};

const areOperatorsAndRolesUpdateRoleAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesUpdateRoleAllowed);
};

const areOperatorsAndRolesDeleteRoleAllowed = () => {
    return hasPermission(UserPermissions.OperatorsAndRolesDeleteRoleAllowed);
};

const isSftpDocumentCreateAllowed = () => {
    return hasPermission(UserPermissions.SftpDocumentCreateAllowed);
};

const isSftpDocumentViewAllowed = () => {
    return hasPermission(UserPermissions.SftpDocumentViewAllowed);
};

const isMerchantTransactionsViewAllowed = () => {
    return hasPermission(UserPermissions.MerchantTransactionsViewAllowed);
};

const isIndividualDocumentsViewAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsViewAllowed);
}

const isIndividualDocumentsDownloadAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsDownloadAllowed);
}

const isIndividualDocumentsUploadAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsUploadAllowed);
}

const isIndividualDocumentsReviewAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsReviewAllowed);
}

const isIndividualDocumentsApproveAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsApproveAllowed);
}

const isIndividualDocumentsManageInternalNoteAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsManageInternalNoteAllowed);
}

const isIndividualDocumentsManageCustomerVisibilityAllowed = () => {
    return hasPermission(UserPermissions.IndividualDocumentsManageCustomerVisibilityAllowed);
}

const isIndividualTransactionViewAllowed = () => {
    return hasPermission(UserPermissions.IndividualTransactionViewAllowed);
};

const isOpenBankingViewUserAllowed = () => {
    return hasPermission(UserPermissions.OpenBankingViewUserAllowed);
};

const isOpenBankingViewTransactionAllowed = () => {
    return hasPermission(UserPermissions.OpenBankingViewTransactionAllowed);
};

const isOpenBankingViewWalletAllowed = () => {
    return hasPermission(UserPermissions.OpenBankingViewWalletAllowed);
};

const isOpenBankingIbanTransferAllowed = () => {
    return hasPermission(UserPermissions.OpenBankingIbanTransferAllowed);
};

const areOperationsAllowed = () => {
    return hasPermission(UserPermissions.OperationsAllowed);
};

const isUserProfileUpdateAllowed = () => {
    return hasPermission(UserPermissions.UserProfileUpdateAllowed);
};

const isKYCAssessAllowed = () => {
    return hasPermission(UserPermissions.KYCAssessAllowed);
};

const isKYCApproveAllowed = () => {
    return hasPermission(UserPermissions.KYCApproveAllowed);
};

const isMerchantProfileUpdateAllowed = () => {
    return hasPermission(UserPermissions.MerchantProfileUpdateAllowed);
};

const isMassPaymentExecutorAllowed = () => {
    try {
        const roles = storageService.getRoles();
        return Array.isArray(roles) && roles.some(role => 
            role?.toLowerCase() === Roles.MassPaymentExecutor.toLowerCase()
        );
    } catch {
        return false;
    }
};

const isOperationOfficer = () => {
    try {
        const roles = storageService.getRoles();
        return Array.isArray(roles) && roles.some(role =>
            role?.toLowerCase() === Roles.OperationOfficer.toLowerCase()
        );
    } catch {
        return false;
    }
};

const isComplianceOfficer = () => {
    try {
        const roles = storageService.getRoles();
        return Array.isArray(roles) && roles.some(role =>
            role?.toLowerCase() === Roles.ComplianceOfficer.toLowerCase()
        );
    } catch {
        return false;
    }
};

const isComplianceOfficerApprover = () => {
    try {
        const roles = storageService.getRoles();
        return Array.isArray(roles) && roles.some(role =>
            role?.toLowerCase() === Roles.ComplianceOfficerApprover.toLowerCase()
        );
    } catch {
        return false;
    }
};

const isNotificationsAllowed = () => {
    try {
        const roles = storageService.getRoles();
        if (!Array.isArray(roles)) {
            return false;
        }
        const lowered = roles.map((r) => r?.toLowerCase());
        return lowered.includes(Roles.SUPER_ADMIN.toLowerCase())
            || lowered.includes(Roles.MerchantMaster.toLowerCase())
            || lowered.includes(Roles.NotificationsManager.toLowerCase());
    } catch {
        return false;
    }
};

const isTennAdmin = () => {
    const token = storageService.getAccessToken();
    if (!token) return false;

    try {
        const roles = JSON.parse(atob(token.split(".")[1])).role || [];
        return roles.some((role: string) => role.toLowerCase() === Roles.TennAdmin.toLowerCase());
    } catch (error) {
        return false;
    }
};

/**
 * Only TennAdmin ("10n Admin") gets the merchant picker — cross-merchant
 * notification management requires the explicit opt-in (TNPDEVAPPS-1812).
 * A Super Admin without TennAdmin operates within their own tenant.
 */
const canManageAllMerchantNotifications = () => {
    return isTennAdmin();
};

const isOperatorsUpdateAllowed = () => {
    return hasPermission(UserPermissions.OperatorsUpdateAllowed);
};

const isMerchantsClientsCreateAllowed = () => {
    return hasPermission(UserPermissions.MerchantsClientsCreateAllowed);
};

const isRollbackTransactionAllowed = () => {
    return hasPermission(UserPermissions.RollbackTransactionAllowed);
}

const isRestrictedToLeadsView = () => {
    return hasPermission(UserPermissions.LeadsPermissionAll);
}

const isBoricaTransactionsAllowed = () => {
    return hasPermission(UserPermissions.BoricaTransactionsViewAllowed);
}


const hasPermission = (permission: string): boolean => {
    if (isSuperAdmin()) {
        return true;
    }

    const permissions = storageService.getPermissions();

    return permissions.some(s => s.toLowerCase() == permission.toLowerCase());
};

const getUserPermittedRoutesForNav = (): Route[] => {
    const routes = getUserPermittedRoutes()
        .filter(f => f.visibleInNav);

    return routes;
};

const canDeleteMerchant = () => {
    return hasPermission(UserPermissions.canDeleteMerchant);
};

const getUserPermittedRoutes = (): Route[] => {
    if (isSuperAdmin()) {
        return SideMenuRoutes;
    }

    const permissions = storageService.getPermissions().map(p => p.toLowerCase());

    return SideMenuRoutes.filter((route) => {
        const routePermissions = Array.isArray(route.permissionName)
            ? route.permissionName.map(p => p.toLowerCase())
            : [route.permissionName.toLowerCase()];

        return permissions.some(p => routePermissions.includes(p));
    });
};

export {
    isDashboardAllowed,
    isMerchantDashboardAllowed,
    isMerchantDashboardUpdateAllowed,
    isCRMUserAllAllowed,
    isCRMUserAllowed,
    isCRMUserCreateNewAllowed,
    isUserProfileViewDetailsAllowed,
    isUserPaymentMethodUpdateAllowed,
    isMerchantAllAllowed,
    isMerchantCreateNewAllowed,
    isMerchantUpdateAllowed,
    isMerchantPaymentMethodUpdateAllowed,
    isIndividualPaymentMethodUpdateAllowed,
    isIndividualDepositAllowed,
    isIndividualWithdrawAllowed,
    isMerchantAllowed,
    isMerchantClientsAllowed,
    isMerchantViewDetailAllowed,
    isWalletsViewAllowed,
    isWalletCreateAllowed,
    isIbanCreateAllowed,
    isWalletCloseAllowed,
    isTariffViewAllowed,
    isTariffUpdateAllowed,
    isKycSubmissionAllAllowed,
    isKycSubmissionViewAllowed,
    isKycSubmissionCreateAllowed,
    isKycDocumentAllAllowed,
    isJournalingAllowed,
    isPortalAdminAllowed,
    isCRMAllAllowed,
    areOperatorsAndRolesAllAllowed,
    areOperatorsAndRolesViewUserAllowed,
    areOperatorsAndRolesCreateNewOperatorAllowed,
    areOperatorsAndRolesUpdateOperatorAllowed,
    areOperatorsAndRolesViewUsersRolesAllowed,
    areOperatorsAndRolesCreateNewRoleAllowed,
    areOperatorsAndRolesUpdateRoleAllowed,
    areOperatorsAndRolesDeleteRoleAllowed,
    isSftpDocumentCreateAllowed,
    isSftpDocumentViewAllowed,
    isMerchantTransactionsViewAllowed,
    isIndividualTransactionViewAllowed,
    isIndividualDocumentsViewAllowed,
    isIndividualDocumentsDownloadAllowed,
    isIndividualDocumentsUploadAllowed,
    isIndividualDocumentsReviewAllowed,
    isIndividualDocumentsApproveAllowed,
    isIndividualDocumentsManageInternalNoteAllowed,
    isIndividualDocumentsManageCustomerVisibilityAllowed,
    getCurrentOperatorUserId,
    isOpenBankingViewUserAllowed,
    isOpenBankingViewTransactionAllowed,
    isOpenBankingViewWalletAllowed,
    isOpenBankingIbanTransferAllowed,
    areOperationsAllowed,
    isUserProfileUpdateAllowed,
    isMerchantProfileUpdateAllowed,
    isOperatorsUpdateAllowed,
    isKYCAssessAllowed,
    isKYCApproveAllowed,
    updatePermissions,
    updateRoles,
    getUserPermittedRoutes,
    getUserPermittedRoutesForNav,
    canDeleteMerchant,
    isMerchantsClientsCreateAllowed,
    isRollbackTransactionAllowed,
    isSuperAdmin,
    isSuperAdminTeams,
    isRestrictedToLeadsView,
    isMassPaymentExecutorAllowed,
    isOperationOfficer,
    isComplianceOfficer,
    isComplianceOfficerApprover,
    isDataManager,
    canSoftDeleteUser,
    isNotificationsAllowed,
    canManageAllMerchantNotifications,
    isTennAdmin,
    isBoricaTransactionsAllowed
};
